import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, ArrowUpRight, ArrowRight, ArrowDownRight, Award, ChevronDown, ChevronUp } from 'lucide-react';
import { getModuleIcon, getFamiliesForModule, getAllowedTiersForGroup, type ModuleId } from '../../../content';
import { getFamilyMasteryKey } from '../../../mastery';
import { Card } from '../../../components/ui/Card';
import { GroupId } from '../../../config/systemConfig';
import { StudentData } from './StudentDetailPanel';
import { PageLayout } from '../../shared/PageLayout';
import { getClassTrendDelta, getClassModuleStats } from '../../../utils/activityHelpers';
interface SubjectsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
}
function humanizeFamilyId(familyId: string): string {
  const parts = familyId.split('-').slice(1);
  return parts.join(' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
}
function getClassFamilyStats(students: StudentData[], moduleId: string, selectedGroup: number) {
  const families = getFamiliesForModule(moduleId as ModuleId);
  const allowedTiers = getAllowedTiersForGroup(selectedGroup as GroupId);
  const relevantFamilies = families.filter(f => allowedTiers.has(f.tier));
  return relevantFamilies.map(f => {
    const key = getFamilyMasteryKey(f.familyId, selectedGroup);
    let totalMastery = 0;
    let practicedCount = 0;
    const strugglingStudents: Array<{
      id: string;
      name: string;
      mastery: number;
    }> = [];
    students.forEach(s => {
      const fm = s.data.familyMastery?.[key];
      if (fm && fm.attempts > 0) {
        practicedCount++;
        totalMastery += fm.masteryScore;
        if (fm.masteryScore < 40) {
          strugglingStudents.push({
            id: s.id,
            name: s.name,
            mastery: Math.round(fm.masteryScore)
          });
        }
      }
    });
    const avgMastery = practicedCount > 0 ? Math.round(totalMastery / practicedCount) : 0;
    return {
      familyId: f.familyId,
      label: humanizeFamilyId(f.familyId),
      avgMastery,
      practicedCount,
      strugglingStudents: strugglingStudents.sort((a, b) => a.mastery - b.mastery)
    };
  }).filter(f => f.practicedCount > 0).sort((a, b) => b.avgMastery - a.avgMastery);
}
function ExpandedModulePanel({
  moduleId,
  students,
  selectedGroup,
  strugglingStudents,
  onSelectStudent
}: {
  moduleId: string;
  students: StudentData[];
  selectedGroup: number;
  strugglingStudents: Array<{
    id: string;
    name: string;
    mastery: number;
  }>;
  onSelectStudent: (id: string) => void;
}) {
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const familyStats = useMemo(() => {
    return getClassFamilyStats(students, moduleId, selectedGroup);
  }, [students, moduleId, selectedGroup]);
  return <motion.div initial={{
    height: 0,
    opacity: 0
  }} animate={{
    height: 'auto',
    opacity: 1
  }} exit={{
    height: 0,
    opacity: 0
  }} className="overflow-hidden bg-slate-50/50" data-id="element-5369">
      <div className="pl-14 pr-4 py-4 border-t border-slate-100 space-y-6" data-id="element-5370">
        {/* Family Table */}
        {familyStats.length > 0 && <div className="space-y-3" data-id="element-5371">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-5372">
              Onderwerpen ({familyStats.length})
            </div>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-5373">
              <div className="divide-y divide-slate-100" data-id="element-5374">
                {familyStats.map(f => {
              const isFamilyExpanded = selectedFamily === f.familyId;
              return <div key={f.familyId} className="flex flex-col" data-id="element-5375">
                      <div className="p-3 flex items-center gap-4 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setSelectedFamily(isFamilyExpanded ? null : f.familyId)} data-id="element-5376">
                        <div className="flex-1 min-w-0" data-id="element-5377">
                          <div className="text-sm font-bold text-slate-900 truncate" data-id="element-5378">
                            {f.label}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-5379">
                            {f.practicedCount} leerlingen
                          </div>
                        </div>
                        <div className="w-32 flex items-center gap-2" data-id="element-5380">
                          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-5381">
                            <div className={`h-full rounded-full ${f.avgMastery >= 75 ? 'bg-emerald-500' : f.avgMastery >= 40 ? 'bg-indigo-400' : 'bg-rose-500'}`} style={{
                        width: `${Math.max(2, f.avgMastery)}%`
                      }} data-id="element-5382" />
                          </div>
                          <div className={`w-8 text-right text-xs font-bold ${f.avgMastery >= 75 ? 'text-emerald-600' : f.avgMastery >= 40 ? 'text-indigo-600' : 'text-rose-600'}`} data-id="element-5383">
                            {f.avgMastery}%
                          </div>
                        </div>
                        <div className="w-6 flex justify-end text-slate-400" data-id="element-5384">
                          {isFamilyExpanded ? <ChevronUp className="w-4 h-4" data-id="element-5385" /> : <ChevronDown className="w-4 h-4" data-id="element-5386" />}
                        </div>
                      </div>

                      <AnimatePresence data-id="element-5387">
                        {isFamilyExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} className="overflow-hidden bg-slate-50/50 border-t border-slate-100" data-id="element-5388">
                            <div className="p-3 pl-4" data-id="element-5389">
                              {f.strugglingStudents.length === 0 ? <div className="text-xs font-medium text-emerald-600" data-id="element-5390">
                                  Geen leerlingen met moeite.
                                </div> : <div className="space-y-2" data-id="element-5391">
                                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-5392">
                                    Aandacht nodig (
                                    {f.strugglingStudents.length})
                                  </div>
                                  <div className="flex flex-wrap gap-2" data-id="element-5393">
                                    {f.strugglingStudents.map(s => {
                            const initial = s.name.charAt(0).toUpperCase();
                            return <div key={s.id} onClick={e => {
                              e.stopPropagation();
                              onSelectStudent(s.id);
                            }} className="flex items-center gap-2 px-2 py-1.5 bg-white border border-slate-200 rounded-lg hover:border-indigo-200 cursor-pointer shadow-sm transition-colors" data-id="element-5394">
                                          <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold" data-id="element-5395">
                                            {initial}
                                          </div>
                                          <span className="text-xs font-bold text-slate-900" data-id="element-5396">
                                            {s.name}
                                          </span>
                                          <span className="text-xs font-black text-rose-600" data-id="element-5397">
                                            {s.mastery}%
                                          </span>
                                        </div>;
                          })}
                                  </div>
                                </div>}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
            })}
              </div>
            </div>
          </div>}

        {/* Module Struggling Students */}
        {strugglingStudents.length === 0 ? <div className="text-sm font-medium text-emerald-600 flex items-center gap-2" data-id="element-5398">
            Alle leerlingen op schema voor dit vak ✓
          </div> : <div className="space-y-2" data-id="element-5399">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-5400">
              Aandacht nodig voor hele vak ({strugglingStudents.length})
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-id="element-5401">
              {strugglingStudents.slice(0, 5).map(s => {
            const initial = s.name.charAt(0).toUpperCase();
            return <div key={s.id} onClick={e => {
              e.stopPropagation();
              onSelectStudent(s.id);
            }} className="flex items-center justify-between p-2 rounded-lg bg-white border border-slate-200 hover:border-indigo-200 cursor-pointer transition-colors" data-id="element-5402">
                    <div className="flex items-center gap-2" data-id="element-5403">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold" data-id="element-5404">
                        {initial}
                      </div>
                      <span className="text-sm font-bold text-slate-900 truncate max-w-[100px]" data-id="element-5405">
                        {s.name}
                      </span>
                    </div>
                    <div className="text-xs font-black text-rose-600" data-id="element-5406">
                      {s.mastery}%
                    </div>
                  </div>;
          })}
            </div>
            {strugglingStudents.length > 5 && <div className="text-xs font-medium text-slate-500 mt-2" data-id="element-5407">
                + {strugglingStudents.length - 5} meer
              </div>}
          </div>}
      </div>
    </motion.div>;
}
export function SubjectsPage({
  students,
  selectedGroup,
  getModuleMastery,
  onSelectStudent
}: SubjectsPageProps) {
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const data = useMemo(() => {
    if (students.length === 0) return null;
    const moduleStats = getClassModuleStats(students, selectedGroup, getModuleMastery);
    const classTrendDelta = getClassTrendDelta(students, selectedGroup);
    // Class average from practiced modules
    const practiced = moduleStats.filter(m => m.practicedCount > 0);
    const classAvg = practiced.length > 0 ? Math.round(practiced.reduce((sum, m) => sum + m.avgMastery, 0) / practiced.length) : 0;
    return {
      classAvg,
      classTrendDelta,
      moduleStats
    };
  }, [students, getModuleMastery, selectedGroup]);
  if (!data) {
    return <PageLayout title="Vakken" description="Analyse per vak" data-id="element-5408">
        <div className="text-center py-20" data-id="element-5409">
          <p className="text-slate-500 font-medium" data-id="element-5410">Geen data beschikbaar.</p>
        </div>
      </PageLayout>;
  }
  return <PageLayout title="Vakken" description="Analyse per vak" data-id="element-5411">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-5412">
        {/* Summary Strip */}
        <Card className="bg-white border-slate-200 shadow-sm overflow-hidden" data-id="element-5413">
          <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-5414">
            <div className="flex-1 p-4 flex items-center gap-4" data-id="element-5415">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-5416">
                <TrendingUp className="w-5 h-5" data-id="element-5417" />
              </div>
              <div data-id="element-5418">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5419">
                  Klasgemiddelde
                </div>
                <div className="text-2xl font-black text-slate-900" data-id="element-5420">
                  {data.classAvg}%
                </div>
              </div>
            </div>

            <div className="flex-1 p-4 flex items-center gap-4" data-id="element-5421">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-5422">
                <ArrowUpRight className="w-5 h-5" data-id="element-5423" />
              </div>
              <div data-id="element-5424">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5425">
                  Trend deze week
                </div>
                <div className="text-2xl font-black text-slate-900" data-id="element-5426">
                  {data.classTrendDelta > 0 ? '+' : ''}
                  {data.classTrendDelta}%
                </div>
              </div>
            </div>

            <div className="flex-1 p-4 flex items-center gap-4" data-id="element-5427">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0" data-id="element-5428">
                <Award className="w-5 h-5" data-id="element-5429" />
              </div>
              <div className="min-w-0" data-id="element-5430">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5431">
                  Sterkste vak
                </div>
                <div className="text-xl font-black text-slate-900 truncate" data-id="element-5432">
                  {data.moduleStats[0]?.moduleTitle || '-'}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Module Bars */}
        <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-5433">
          <div className="divide-y divide-slate-100" data-id="element-5434">
            {data.moduleStats.filter(m => m.practicedCount > 0).map((stat, i) => {
            const IconComp = getModuleIcon(stat.moduleIcon);
            const total = students.length;
            const isExpanded = selectedModule === stat.moduleId;
            const strugglingStudents = students.map(s => ({
              ...s,
              mastery: getModuleMastery(s.id, stat.moduleId)
            })).filter(s => s.mastery < 40).sort((a, b) => a.mastery - b.mastery);
            return <motion.div key={stat.moduleId} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.03
            }} className="hover:bg-slate-50 transition-colors" data-id="element-5435">
                    <div className="p-3 flex items-center gap-4 cursor-pointer" role="button" tabIndex={0} aria-expanded={isExpanded} aria-label={`${stat.moduleTitle} — ${stat.avgMastery}% gemiddeld${isExpanded ? ', ingeklapt' : ', klik om uit te klappen'}`} onClick={() => setSelectedModule(isExpanded ? null : stat.moduleId)} onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedModule(isExpanded ? null : stat.moduleId);
                }
              }} data-id="element-5436">
                      <div className="flex items-center gap-3 w-48 flex-shrink-0" data-id="element-5437">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500" data-id="element-5438">
                          <IconComp className="w-4 h-4" data-id="element-5439" />
                        </div>
                        <div className="min-w-0" data-id="element-5440">
                          <div className="font-bold text-sm text-slate-900 truncate" data-id="element-5441">
                            {stat.moduleTitle}
                          </div>
                          <div className="text-[10px] font-medium text-slate-500" data-id="element-5442">
                            {stat.practicedCount} leerlingen
                          </div>
                        </div>
                      </div>

                      <div className="flex-1 flex items-center gap-4" data-id="element-5443">
                        <div className="flex-1 space-y-1" data-id="element-5444">
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex" data-id="element-5445">
                            {/* Distribution segments */}
                            {stat.distribution.excellent > 0 && <div className="h-full bg-emerald-500" style={{
                        width: `${stat.distribution.excellent / total * 100}%`
                      }} title={`${stat.distribution.excellent} uitstekend`} data-id="element-5446" />}
                            {stat.distribution.good > 0 && <div className="h-full bg-indigo-400" style={{
                        width: `${stat.distribution.good / total * 100}%`
                      }} title={`${stat.distribution.good} goed`} data-id="element-5447" />}
                            {stat.distribution.attention > 0 && <div className="h-full bg-rose-500" style={{
                        width: `${stat.distribution.attention / total * 100}%`
                      }} title={`${stat.distribution.attention} aandacht nodig`} data-id="element-5448" />}
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" title={`Klasgemiddelde: ${stat.avgMastery}%`} data-id="element-5449">
                            <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{
                        width: `${Math.max(2, stat.avgMastery)}%`
                      }} data-id="element-5450" />
                          </div>
                        </div>
                        <div className="w-10 text-right text-base font-black text-slate-900" data-id="element-5451">
                          {stat.avgMastery}%
                        </div>
                        <div className="w-6 flex justify-end" data-id="element-5452">
                          {stat.trend === 'up' && <ArrowUpRight className="w-4 h-4 text-emerald-500" data-id="element-5453" />}
                          {stat.trend === 'down' && <ArrowDownRight className="w-4 h-4 text-rose-500" data-id="element-5454" />}
                          {stat.trend === 'stable' && <ArrowRight className="w-4 h-4 text-slate-300" data-id="element-5455" />}
                        </div>
                        <div className="w-6 flex justify-end text-slate-400" data-id="element-5456">
                          {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-5457" /> : <ChevronDown className="w-5 h-5" data-id="element-5458" />}
                        </div>
                      </div>
                    </div>

                    <AnimatePresence data-id="element-5459">
                      {isExpanded && <ExpandedModulePanel moduleId={stat.moduleId} students={students} selectedGroup={selectedGroup} strugglingStudents={strugglingStudents} onSelectStudent={onSelectStudent} data-id="element-5460" />}
                    </AnimatePresence>
                  </motion.div>;
          })}
          </div>
          <div className="bg-slate-50 p-3 border-t border-slate-100 flex justify-center gap-6 text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-5461">
            <div className="flex items-center gap-1.5" data-id="element-5462">
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" data-id="element-5463" />{' '}
              Uitstekend
            </div>
            <div className="flex items-center gap-1.5" data-id="element-5464">
              <div className="w-2.5 h-2.5 rounded-sm bg-indigo-400" data-id="element-5465" /> Goed
            </div>
            <div className="flex items-center gap-1.5" data-id="element-5466">
              <div className="w-2.5 h-2.5 rounded-sm bg-rose-500" data-id="element-5467" /> Aandacht
            </div>
          </div>
        </Card>
      </div>
    </PageLayout>;
}